import React, {FC, useContext} from "react";
import {Card, CardContent} from "@components/ui/card";
import {cn} from "@utils";
import {ContextAppState} from "@app/context.ts";
import {useAutoRetry, useLeaderboardGet} from "@hooks";
import {Skeleton} from "@components/ui/skeleton.tsx";

const leaderboard = [
    {
        user: 2,
        wallet: "DYdVqQCeFhT3gmECWqVvMAkg7qjmJjzXYiDFaznJQv96",
        points: 250,
        rank: 1,
    },
    {
        user: 1,
        wallet: "2YVqrZsSQrwD14NpmPQ4z5PQksVjxskA9jDqc9wEippZ",
        points: 150,
        rank: 2,
    },
    {
        user: 39,
        wallet: "3As6vjZBDAxMVDnqRBRKTJBfDNgQa13L9sSVH65VMxEP",
        points: 100,
        rank: 3,
    },
];


export const LeaderboardPage = () => {
    const appState = useContext(ContextAppState);

    const [leaderboard, loading, error, reload] = useLeaderboardGet();
    const MAX_ATTEMPTS = 5;

    const [retryCount, retrying, secondsRemaining, resetRetry] = useAutoRetry({
        shouldRetry: !leaderboard && !!error,
        maxAttempts: MAX_ATTEMPTS,
        retryCallback: reload,
    });

    if (!leaderboard || loading || retrying) {
        return (
            <div className="pt-10 flex flex-col items-center space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                    {Array(6).fill(null).map((_, index) => (
                        <Skeleton key={index} className="h-16 w-full rounded-lg"/>
                    ))}
                </div>
                {retrying && (
                    <p className="text-gray-500 text-md">
                        Failed to load. Trying again ({retryCount + 1}/ {MAX_ATTEMPTS})...
                    </p>
                )}

                {retrying && secondsRemaining > 0 && (
                    <p className="text-gray-500 text-sm">
                        Retrying in {secondsRemaining}s
                    </p>
                )}
            </div>
        );
    }

    if (error && retryCount >= MAX_ATTEMPTS) {
        return (
            <div className="pt-32 flex flex-col space-y-6 items-center justify-center text-center">
                <p className="text-red-500">Failed to load rules after {MAX_ATTEMPTS} attempts.</p>
                <button
                    onClick={() => {
                        resetRetry();
                        reload();
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                >
                    Try again
                </button>
            </div>
        );
    }

    const currentUserId = appState.auth.user.id;
    return (
        <div className="max-w-xl mx-auto p-4">
            <h2 className="text-2xl font-bold mb-4 text-center text-orange-400">
                Leaderboard
            </h2>
            <div className="space-y-2">
                {leaderboard.map((row) => (
                    <Row user={row.user.id}
                         wallet={row.user.wallet}
                         points={row.points}
                         rank={row.rank}
                         current={currentUserId}
                    />
                ))}
            </div>
        </div>

    );
}

type RowProps = {
    current: string;
    user: string;
    wallet: string;
    points: number;
    rank: number,
}

const Row: FC<RowProps> = React.memo(({current, user, wallet, points, rank}) => {
    const isCurrent = current === user;

    return (
        <Card
            key={user}
            className={cn(
                "w-full flex items-center justify-between rounded-2xl border shadow-xl bg-gradient-to-br from-orange-100/30 via-orange-50/10 to-transparent backdrop-blur-sm transition-all",
                isCurrent ? "border-orange-400 border-2" : "border-orange-200/60"
            )}
        >
            <CardContent className="flex justify-between w-full items-center p-4">
                <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-orange-400">#{rank}</span>
                    <span className="text-sm font-mono text-orange-200">
            {wallet.slice(0, 4)}...{wallet.slice(-4)}
          </span>
                    {isCurrent && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-orange-400 text-white rounded-full">
              You
            </span>
                    )}
                </div>
                <div className="text-lg font-bold text-orange-500">{points} pts</div>
            </CardContent>
        </Card>
    );
});